import useMySoftwareProviders from '@hooks/query/software-provider/useMySoftwareProviders';
import { PlusOutlined } from '@icon';
import { Drawer, IconButton, Modal, Table, TableProps, Typography, useIsAuthorized } from '@ui';
import React from 'react';
import { TAuthority } from 'types/authority.types';
import { Full } from 'types/common';
import { TMyMerchantSearchParams, TMyProvider } from 'types/software-provider.types';
import AddMerchantModal from './components/add-merchant-modal';
import DeleteMerchantModal from './components/delete-merchant-modal';
import GenerateLegacyCode from './components/generate-legacy-code/generate-legacy-code';
import { TableSC } from './software-providers.styled';
import { useTableColumns } from './software-providers.utils';

const EDIT_AUTH: TAuthority[] = ['apiKeyManagementEdit'];

type TTableProps = Full<TableProps<TMyProvider>>;

const INITIAL_SEARCH_PARAMS: TMyMerchantSearchParams = {
  sortBy: 'createdAt',
  direction: 'DESC',
  page: 1,
  pageSize: 30,
};

const INITIAL_TABLE_STATE: TTableProps['initialState'] = {
  sortBy: [{ id: 'createdAt', desc: true }],
  pageIndex: 0,
  pageSize: 30,
};

const SoftwareProviders: React.FunctionComponent = () => {
  const [searchParams, setSearchParams] =
    React.useState<TMyMerchantSearchParams>(INITIAL_SEARCH_PARAMS);
  const { isFetching, data, refetch } = useMySoftwareProviders(searchParams, {
    keepPreviousData: true,
  });
  const canEdit = useIsAuthorized(EDIT_AUTH);

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    setSearchParams((o) => ({
      ...o,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: sortBy as any,
      direction: sortDirection,
    }));
  }, []);

  const { close: closeAdd, drawerProps, open: openAdd } = Drawer.useDrawer();
  const {
    close: closeDelete,
    open: openDelete,
    data: deleteData,
    modalProps,
  } = Modal.useModal<TMyProvider | undefined>();
  const columns = useTableColumns({ onDeleteClick: openDelete, canEdit });

  const afterDelete = React.useCallback(() => {
    refetch();
    closeDelete();
  }, []);

  const afterAdd = React.useCallback(() => {
    refetch();
    closeAdd();
  }, []);

  return (
    <>
      <GenerateLegacyCode canEdit={canEdit} />
      <Table.Header
        title={
          (
            <Typography display="h5" as="span">
              {`Externé služby (${data?.pagination.total ?? 0})`}
            </Typography>
          ) as any
        }
        extra={
          canEdit ? (
            <IconButton
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                openAdd();
              }}
            />
          ) : null
        }
      />
      <TableSC
        loading={isFetching}
        manualSortBy
        disableMultiSort
        manualPagination
        autoResetPage
        columns={columns}
        data={data?.items ?? []}
        initialState={INITIAL_TABLE_STATE}
        pagination={data?.pagination}
        onChange={handleTableChange}
        showEmpty
        emptyTitle="Žiadne Externé služby"
      />
      <AddMerchantModal onClose={closeAdd} {...drawerProps} afterSubmit={afterAdd} />
      <DeleteMerchantModal
        {...modalProps}
        item={deleteData}
        onClose={closeDelete}
        afterSubmit={afterDelete}
      />
    </>
  );
};

export default React.memo(SoftwareProviders);
